import { defineStore } from "pinia";
import { useMasterData } from "./master-data";
import { useExperienceRawApi } from "@/composables/useExperienceRawApi";
import { DistributionContent, ExperienceSource, RawElement } from "@/types/generated/ExperienceRawServiceApi";
import { RawAsterixAdapterInformation, RawLegacyAdapterInformation } from "@/features/experience-raw/legacy-asx-types";
import { checkFormValidity, getRequiredFields } from "@/utils/form-validator";
import { FormField } from "@/types/Form";
import { MixedHighlightValue } from "@/types/Highlights";
import { mapGenericToManageableHighlight, mixedHighlightListValidator } from "@/utils/mixed-highlights";
import { Experience as OfferExperience, ExperienceType } from "@/types/generated/OfferServiceApiOld";
import { Option } from "@/types/generated/OfferServiceApi";
import { useOfferServiceApi } from "@/composables/useOfferServiceApi";
import { INSTANT_DURATION } from "@/constants/date.constants";

import {
  ExperienceLocationData,
  useExperienceLocationStore,
} from "@/features/experience-shared/stores/useExperienceLocationStore";
import { useRefundPoliciesStore } from "@/features/experience-shared/stores/useRefundPoliciesStore";
import { usePickupExperienceApi } from "@/features/experience-calendar/api/usePickupExperienceApi";
import { getChangedValues } from "@/features/experience-shared/utils/get-changed-values";
import { getExperienceRawInitialValues } from "@/features/experience-raw/utils/experience-raw-utils";
import objectAssignDeep from "object-assign-deep";
import { waitForTimeout } from "@/features/core-shared/utils/promise/wait";
import { updateDelayMs } from "@/features/experience-shared/constants/experience-shared-constants";
import { useDistributionAttributes } from "@/features/experience-shared/composables/useDistributionAttributes";
import { shouldLoadOfferExperience } from "@/utils/should-load-offer-experience";

const isSaving = ref(false);

interface ExperienceRawContentGeneration {
  description: FormField<string, true>;
  additional_description: FormField<string>;
  highlights: FormField<MixedHighlightValue, true>;
  included: FormField<MixedHighlightValue, true>;
  non_included: FormField<MixedHighlightValue>;
  important_information: FormField<MixedHighlightValue>;
  meeting_point_details: FormField<string, false>;
  markets: FormField<string[]>;
}

// these properties are optional because for ASX products we do not manage operational data(offer experience)
interface ExperienceRawPricingAndAvailability {
  experience_type?: FormField<ExperienceType>;
  currency?: FormField<string, true>;
  instant_confirmation?: FormField<boolean, false>;
  confirmation_time?: FormField<string, boolean>;
  cutoff_time?: FormField<string>;
}

// these properties are optional because for ASX products we do not manage operational data(offer experience)
interface ExperienceRawOptions {
  options?: FormField<Option[], false>;
}

interface ExperienceRawLegacyAdapterInformation {
  asterix_service_and_modalities_codes?: FormField<RawLegacyAdapterInformation["asx_codes"], boolean>;
}

export type ExperienceRawForm = ExperienceRawContentGeneration &
  Partial<ExperienceRawPricingAndAvailability> &
  Partial<ExperienceRawOptions> &
  ExperienceRawLegacyAdapterInformation;

export type ExperienceRawData = RawElement & {
  options: Option[];
  offerExperience?: OfferExperience;
} & {
  // supplier_id and product type will be loaded from distribution-content
  supplier_id: string;
  product_type: DistributionContent["experience_source"];
};

export interface ExperienceRawDocument {
  fields: ExperienceRawForm;
  modified: boolean;
  data: ExperienceRawData;
}

export interface ExperienceRawState {
  rawContents: {
    [key: string]: ExperienceRawDocument;
  };
}

function mapFormToExperienceRawService(
  fields: ExperienceRawForm,
  locationFormData: ExperienceLocationData | undefined,
  additionalCitiesData: string[] | undefined,
  venuesData: string[] | undefined,
  data: ExperienceRawData
): Omit<RawElement, "experience_id" | "status_code" | "flow_code"> {
  return {
    functional: {
      // NOTE: highlights, included, non_included, important_information, and additional services (via typed fields)
      // are now managed by the section stores and should not be included here to avoid conflicts during save operations
      markets: fields.markets.value,
      ...(locationFormData ? { location: locationFormData } : {}),
      ...(additionalCitiesData ? { additional_cities: additionalCitiesData } : {}),
      ...(venuesData ? { venues: venuesData } : {}),
    },
    commercial: {
      title: data.commercial.title,
      description: fields.description.value,
      additional_description: fields.additional_description.value,
      meeting_point_details: fields.meeting_point_details.value,
    },
    ...mapLegacyAdapterInformationToPayload(fields),
  };
}

function mapPayloadToContentGeneration(data: RawElement): ExperienceRawContentGeneration {
  const masterData = useMasterData();
  return {
    description: {
      value: data.commercial.description ?? "",
      required: true,
      category: "experience_info",
    },
    additional_description: {
      value: data.commercial.additional_description ?? "",
      required: false,
      category: "experience_info",
    },
    highlights: {
      value: {
        premade: data.functional?.highlights?.map((code) => masterData.getHighlightByCode("highlights", code)) ?? [],
        custom: mapGenericToManageableHighlight(data.commercial.custom_highlights ?? []),
      },
      required: true,
      category: "experience_info",
      validator: mixedHighlightListValidator,
    },
    included: {
      value: {
        premade: data.functional?.included?.map((code) => masterData.getHighlightByCode("included", code)) ?? [],
        custom: mapGenericToManageableHighlight(data.commercial.custom_included ?? []),
      },
      required: true,
      category: "experience_info",
      validator: mixedHighlightListValidator,
    },
    non_included: {
      value: {
        premade: data.functional?.non_included?.map((code) => masterData.getHighlightByCode("included", code)) ?? [],
        custom: mapGenericToManageableHighlight(data.commercial.custom_non_included ?? []),
      },
      required: false,
      category: "experience_info",
      validator: mixedHighlightListValidator,
    },
    important_information: {
      value: {
        premade:
          data.functional?.important_information?.map((code) =>
            masterData.getHighlightByCode("importantInformation", code)
          ) ?? [],
        custom: mapGenericToManageableHighlight(data.commercial.custom_important_information ?? []),
      },
      required: false,
      category: "experience_info",
      validator: mixedHighlightListValidator,
    },
    meeting_point_details: {
      value: data.commercial.meeting_point_details || "",
      required: false,
      category: "experience_location",
    },
    markets: {
      value: data.functional?.markets ?? [],
      required: false,
      category: "segmentation_details",
    },
  };
}

function mapPayloadToPricingAndAvailability(data: OfferExperience): ExperienceRawPricingAndAvailability {
  return {
    experience_type: {
      value: data.type,
      required: false,
      category: "experience_pricing_and_availability",
    },
    currency: {
      value: data.currency,
      required: true,
      category: "experience_pricing_and_availability",
    },
    instant_confirmation: {
      value: data.confirmation_time === INSTANT_DURATION,
      required: false,
      category: "experience_pricing_and_availability",
    },
    confirmation_time: {
      value: data.confirmation_time,
      required: false,
      category: "experience_pricing_and_availability",
      validator: (val) => val !== INSTANT_DURATION,
    },
    cutoff_time: {
      value: data.cutoff_time,
      required: false,
      category: "experience_pricing_and_availability",
    },
  };
}

function mapPayloadToOptions(data: Option[]): ExperienceRawOptions {
  return {
    options: {
      value: data ?? [],
      required: false,
      category: "experience_info",
    },
  };
}

function mapPayloadToLegacyAdapterInformation(
  productType: DistributionContent["experience_source"],
  data: ExperienceRawData
): ExperienceRawLegacyAdapterInformation | {} {
  if (productType !== "ASX") return {};

  return {
    asterix_service_and_modalities_codes: {
      value: (data as any).legacy_adapter_information?.asx_codes ?? [],
      required: true,
      category: "asterix_integration",
      validator: validateAsterixAdapterInformation,
    },
  };
}

export function validateAsterixAdapterInformation(valueList: RawAsterixAdapterInformation[]) {
  return (
    !!valueList &&
    valueList.length > 0 &&
    valueList.every((value) => (value.code?.length ?? 0) > 0 && (value.modality_codes?.length ?? 0) > 0)
  );
}

function mapLegacyAdapterInformationToPayload(formFields: ExperienceRawForm): {
  legacy_adapter_information?: RawLegacyAdapterInformation;
} {
  if (formFields.asterix_service_and_modalities_codes?.value === undefined) {
    return {};
  }

  return {
    legacy_adapter_information: {
      asx_codes: formFields.asterix_service_and_modalities_codes?.value ?? [],
    },
  };
}

export const useExperienceRaw = defineStore("experience-raw", {
  state: (): ExperienceRawState => {
    return {
      rawContents: {},
    };
  },
  getters: {
    isSaving: () => isSaving.value,
    requiredFields:
      (state) =>
      (experienceId: string): Partial<ExperienceRawForm> => {
        const form = state.rawContents[experienceId];

        return getRequiredFields(form.fields);
      },
    submitEnabled:
      (state) =>
      (experienceId: string): boolean => {
        const form = state.rawContents[experienceId];

        return checkFormValidity(form.fields);
      },
  },
  actions: {
    createDocument(id: string, data: ExperienceRawData, productType: ExperienceSource) {
      const fields = {
        ...mapPayloadToContentGeneration(data),
        ...(shouldLoadOfferExperience(productType) ? mapPayloadToPricingAndAvailability(data.offerExperience!) : {}),
        ...(shouldLoadOfferExperience(productType) ? mapPayloadToOptions(data.options) : {}),
        ...mapPayloadToLegacyAdapterInformation(productType, data),
      };

      this.rawContents[id] = {
        modified: false,
        fields,
        data,
      };
    },

    async refetchOptions(id: string) {
      if (this.rawContents[id]?.fields?.experience_type?.value !== this.rawContents[id]?.data?.offerExperience?.type) {
        // we need to refresh the options, because when we change the `experience_type` on pricing and availability page, BE returns options with new status code
        // so we need to get new options to display the correct icon on the left sidebar
        // see `raw.vue` for the source of this refresh
        await refreshNuxtData(`get-raw-document-${id}`);
      }
    },

    async getRawDocument(id: string) {
      const locationStore = useExperienceLocationStore();
      const refundPoliciesStore = useRefundPoliciesStore();
      const { getExperienceRawV2ByExperienceId } = useExperienceRawApi();
      const { getExperience: getOfferExperience } = useOfferServiceApi();
      const { getExperienceOptions } = useOfferServiceApi();
      const { getDistributionContent } = useExperienceRawApi();
      const { data: distributionContent } = await getDistributionContent(id);
      const supplierId = distributionContent?.supplier_id!;
      const productType: ExperienceSource = distributionContent?.experience_source ?? ExperienceSource.NOVA;

      const [rawExp, offerExp, options] = await Promise.all([
        getExperienceRawV2ByExperienceId(id),
        shouldLoadOfferExperience(productType) ? getOfferExperience(id) : null,
        shouldLoadOfferExperience(productType) ? getExperienceOptions(id) : null,
        shouldLoadOfferExperience(productType) ? refundPoliciesStore.loadRefundPolicies(id) : null,
      ]);

      const data = {
        ...rawExp.data,
        offerExperience: offerExp ? offerExp.data : undefined,
        options: options?.data || [],
        supplier_id: supplierId,
        supplier: supplierId,
        product_type: productType,
      };

      this.createDocument(id, data, productType);
      locationStore.initLocationFields(data.functional?.location);
      locationStore.initAdditionalCitiesField(data.functional?.additional_cities ?? []);
      locationStore.initVenuesField(data.functional?.venues ?? []);
    },

    async updateRawDocument(id: string, options?: { publish?: boolean }) {
      isSaving.value = true;

      const { publish = false } = options || {};
      const document = this.rawContents[id];

      if (!document) {
        return;
      }

      const { patchExperienceRaw } = useExperienceRawApi();
      try {
        const rawId = document.data.id as string;
        const { saveData: saveDistributionAttributes } = useDistributionAttributes();

        const rawExperiencePayload = getRawExperiencePayload(id, document, publish);

        await saveDistributionAttributes(id);

        if (rawExperiencePayload) {
          await patchExperienceRaw(rawId, rawExperiencePayload);
        }

        // todo remove delay OFF-2547
        await waitForTimeout(updateDelayMs);

        // update changed values using objectAssignDeep because with this lib, by default arrays in later objects will overwrite earlier values
        document.data = objectAssignDeep({}, document.data, rawExperiencePayload);
        document.modified = false;
      } finally {
        isSaving.value = false;
      }
    },

    /**
     * this is a temporary solution, this logic will be deleted (hopefully)
     *
     * if the user changes supplier id we need to update pickup for each option
     */
    async updateSupplierIdForPickups(experienceId: string, newSupplierId: string) {
      const offerService = useOfferServiceApi();
      const pickupExperienceApi = usePickupExperienceApi();

      const { data: options } = await offerService.getOptions(experienceId);

      const updatePromises = options.map(async (option) => {
        // Fetch pickup by option ID
        const { data: pickup } = await pickupExperienceApi.getPickupsByOptionId(option.id!);

        // Update pickup's supplier_id
        if (pickup?.id) {
          const { id: _id, ...pickupWithoutId } = pickup;
          await pickupExperienceApi.updatePickup(pickup.id, { ...pickupWithoutId, supplier_id: newSupplierId });
        }
      });

      await Promise.all(updatePromises);
    },

    // temporary
    async saveSupplierId(
      experienceId: string,
      { isSupplierChanged, payload }: { isSupplierChanged: boolean; payload: Partial<OfferExperience> | null }
    ) {
      const experienceRawApi = useExperienceRawApi();
      const offerApi = useOfferServiceApi();

      // skip if its not updated
      if (!payload) {
        return;
      }

      // if supplier changed we need to update distribution-content and pickups
      if (isSupplierChanged && payload.supplier) {
        await experienceRawApi.patchDistributionContent(experienceId, { supplier_id: payload.supplier });
        await this.updateSupplierIdForPickups(experienceId, payload.supplier);
      }

      await offerApi.updateExperience(experienceId, payload);
    },
  },
});

function getRawExperiencePayload(
  experienceId: string,
  experience: ExperienceRawDocument,
  publish: boolean
): Partial<RawElement> | null {
  const locationStore = useExperienceLocationStore();

  const initialRawFields = getExperienceRawInitialValues(experience.data);
  const updatedRawFields = mapFormToExperienceRawService(
    experience.fields,
    locationStore.getLocationPayload(),
    locationStore.getAdditionalCitiesPayload(),
    locationStore.getVenuesPayload(),
    experience.data
  );
  const changedValues = getChangedValues(initialRawFields, updatedRawFields);
  // if user is trying to publish we force isRawChanged to true
  const isRawChanged = Object.keys(changedValues).length > 0 || publish;

  const rawUpdatePayload: Partial<RawElement> = {
    ...changedValues,
    experience_id: experienceId,
  };
  return isRawChanged ? rawUpdatePayload : null;
}
